import { Capacitor, registerPlugin } from '@capacitor/core';
import { datadogRum } from '@datadog/browser-rum';

import pickBy from 'lodash/pickBy';
import { DeviceInfo } from 'src/constants/LocalStorageKeys';

/**
 * What the shell can supply, mirroring the native `DeviceVariableKey` entries. Every key is optional
 * and only resolved keys are present, so absence means "the MDM did not provision this".
 */
export interface DeviceConfigResult {
    deviceUniqueId?: string;
    serialNumber?: string;
    imei?: string;
    wifiMac?: string;
    udid?: string;
    deviceName?: string;
    iccid?: string;
    phoneNumber?: string;
    userEmail?: string;
}

interface DeviceConfigPlugin {
    getConfig(): Promise<DeviceConfigResult>;
}

/**
 * Implemented in the native shell, not here — see
 * [`DeviceConfigPlugin`](https://github.com/hlosg/mobile/blob/main/android/app/src/main/java/com/sagehealth/app/plugin/DeviceConfigPlugin.kt).
 * `registerPlugin` only builds a proxy whose calls become bridge round-trips, so this string has to
 * match the name the native side registers itself under (`@CapacitorPlugin(name = "DeviceConfig")`);
 * a mismatch shows up as a rejected call at runtime rather than as a build failure.
 */
const DeviceConfig = registerPlugin<DeviceConfigPlugin>('DeviceConfig');

/**
 * Reads device identity from the native shell. Returns null in a browser, and also when the shell
 * predates the plugin — an older wrapper must not break the web app.
 */
export async function readNativeDeviceConfig(): Promise<DeviceConfigResult | null> {
    if (!Capacitor.isNativePlatform()) {
        return null;
    }
    try {
        return await DeviceConfig.getConfig();
    } catch (error) {
        datadogRum.addError(error, { context: 'readNativeDeviceConfig' });
        return null;
    }
}

/**
 * Projects the native contract onto the DeviceInfo shape the app already stores. `deviceUniqueId` is
 * the MDM's device id, which this app has always called `deviceId` (Hexnode `%deviceid%`).
 */
export function toDeviceInfo(config: DeviceConfigResult): Partial<DeviceInfo> {
    // The contract carries more than this — deviceName, iccid, userEmail, wifiMac are supplied but
    // nothing reads them, so they are not stored.
    const mapped: Partial<DeviceInfo> = {
        deviceId: config.deviceUniqueId,
        serialNumber: config.serialNumber,
        imei: config.imei,
        udid: config.udid,
        phoneNumber: config.phoneNumber,
    };

    // Drop unresolved keys rather than storing undefined over an existing value.
    return pickBy(mapped, Boolean);
}
